import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import type { DocumentInfoLibraryProps, DocumentInfoSize } from "../types";
import {
  estimateReadingTimeSeconds,
  formatReadingTime,
} from "../utils/estimate-reading-time";
import { PILL_ORDER, ReadingTimePill, SentByPill } from "./pills";

const READING_TIME_PLACEHOLDER = "< 1 min";

// Unresolved dynamic variables (`{{…}}`) show the fallback in the editor preview; the reader resolves them.
const isUnresolved = (value: string | null | undefined) =>
  !value || value.startsWith("{{");

// Published pages carry the full story JSON as `window.story` (see rendering-provider templates).
const getReaderReadingTime = (): string | null => {
  if (typeof window === "undefined") return null;
  const story = (window as { story?: { slides?: unknown[] } }).story;
  if (!Array.isArray(story?.slides)) return null;
  return formatReadingTime(
    estimateReadingTimeSeconds({
      nodes: story.slides,
      slideCount: story.slides.length,
    }),
  );
};

export const DocumentInfoLibrary = ({
  entity,
  fallbackSenderName,
  fallbackSenderPicture,
  readingTime,
}: {
  entity: StaticEntityType;
  fallbackSenderName?: string;
  fallbackSenderPicture?: string | null;
  readingTime?: string;
}) => {
  const { settings } = entity as StaticEntityType<DocumentInfoLibraryProps>;
  const size: DocumentInfoSize = settings.size ?? "small";

  const enabled = settings.pills ?? PILL_ORDER;
  const pills = PILL_ORDER.filter((p) => enabled.includes(p));

  const senderName = isUnresolved(settings.senderName)
    ? (fallbackSenderName ?? "Sender")
    : settings.senderName!;
  const senderPicture = isUnresolved(settings.senderPicture)
    ? fallbackSenderPicture
    : settings.senderPicture;

  const time =
    readingTime ?? getReaderReadingTime() ?? READING_TIME_PLACEHOLDER;

  return (
    <div
      className={cn(
        "tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2",
        settings.horizontalAlignment === "center" && "tw-justify-center",
        settings.horizontalAlignment === "end" && "tw-justify-end",
      )}
    >
      {pills.map((pill) => {
        if (pill === "sentBy")
          return (
            <SentByPill
              key="sentBy"
              size={size}
              name={senderName}
              picture={senderPicture}
            />
          );
        if (pill === "readingTime")
          return (
            <ReadingTimePill key="readingTime" size={size} time={time} />
          );
        return null;
      })}
    </div>
  );
};
